import i18n from 'i18next'

// Import all available languages directly to ensure they're available
import arLanguages from '../data/ar/languages.json'
import arSubjects from '../data/ar/subjects.json'
import daLanguages from '../data/da/languages.json'
import daSubjects from '../data/da/subjects.json'
import deLanguages from '../data/de/languages.json'
import deSubjects from '../data/de/subjects.json'
import elLanguages from '../data/el/languages.json'
import elSubjects from '../data/el/subjects.json'
import enLanguages from '../data/en/languages.json'
import enSubjects from '../data/en/subjects.json'
import esLanguages from '../data/es/languages.json'
import esSubjects from '../data/es/subjects.json'
import fiLanguages from '../data/fi/languages.json'
import fiSubjects from '../data/fi/subjects.json'
import frLanguages from '../data/fr/languages.json'
import frSubjects from '../data/fr/subjects.json'
import heLanguages from '../data/he/languages.json'
import heSubjects from '../data/he/subjects.json'
import huLanguages from '../data/hu/languages.json'
import huSubjects from '../data/hu/subjects.json'
import itLanguages from '../data/it/languages.json'
import itSubjects from '../data/it/subjects.json'
import jaLanguages from '../data/ja/languages.json'
import jaSubjects from '../data/ja/subjects.json'
import koLanguages from '../data/ko/languages.json'
import koSubjects from '../data/ko/subjects.json'
import nbLanguages from '../data/nb/languages.json'
import nbSubjects from '../data/nb/subjects.json'
import nlLanguages from '../data/nl/languages.json'
import nlSubjects from '../data/nl/subjects.json'
import plLanguages from '../data/pl/languages.json'
import plSubjects from '../data/pl/subjects.json'
import ptLanguages from '../data/pt/languages.json'
import ptSubjects from '../data/pt/subjects.json'
import roLanguages from '../data/ro/languages.json'
import roSubjects from '../data/ro/subjects.json'
import ruLanguages from '../data/ru/languages.json'
import ruSubjects from '../data/ru/subjects.json'
import svLanguages from '../data/sv/languages.json'
import svSubjects from '../data/sv/subjects.json'
import trLanguages from '../data/tr/languages.json'
import trSubjects from '../data/tr/subjects.json'
import zhLanguages from '../data/zh/languages.json'
import zhSubjects from '../data/zh/subjects.json'

// Create a map of embedded data for all 22 languages
const embeddedData = {
  ar: { languages: arLanguages, subjects: arSubjects },
  da: { languages: daLanguages, subjects: daSubjects },
  de: { languages: deLanguages, subjects: deSubjects },
  el: { languages: elLanguages, subjects: elSubjects },
  en: { languages: enLanguages, subjects: enSubjects },
  es: { languages: esLanguages, subjects: esSubjects },
  fi: { languages: fiLanguages, subjects: fiSubjects },
  fr: { languages: frLanguages, subjects: frSubjects },
  he: { languages: heLanguages, subjects: heSubjects },
  hu: { languages: huLanguages, subjects: huSubjects },
  it: { languages: itLanguages, subjects: itSubjects },
  ja: { languages: jaLanguages, subjects: jaSubjects },
  ko: { languages: koLanguages, subjects: koSubjects },
  nb: { languages: nbLanguages, subjects: nbSubjects },
  nl: { languages: nlLanguages, subjects: nlSubjects },
  pl: { languages: plLanguages, subjects: plSubjects },
  pt: { languages: ptLanguages, subjects: ptSubjects },
  ro: { languages: roLanguages, subjects: roSubjects },
  ru: { languages: ruLanguages, subjects: ruSubjects },
  sv: { languages: svLanguages, subjects: svSubjects },
  tr: { languages: trLanguages, subjects: trSubjects },
  zh: { languages: zhLanguages, subjects: zhSubjects },
}

export const getCurrentLanguage = () => {
  const lang = i18n.language || 'en'
  return lang
}

// Synchronous version - returns embedded data directly
export const loadLanguagesSync = () => {
  const currentLang = getCurrentLanguage()
  if (embeddedData[currentLang] && embeddedData[currentLang].languages) {
    return embeddedData[currentLang].languages
  }
  // Fallback to English
  if (embeddedData.en && embeddedData.en.languages) {
    return embeddedData.en.languages
  }
  return []
}

// Synchronous version - returns embedded data directly
export const loadSubjectsSync = () => {
  const currentLang = getCurrentLanguage()
  if (embeddedData[currentLang] && embeddedData[currentLang].subjects) {
    return embeddedData[currentLang].subjects
  }
  // Fallback to English
  if (embeddedData.en && embeddedData.en.subjects) {
    return embeddedData.en.subjects
  }
  return []
}

export const loadLanguages = async () => {
  try {
    const currentLang = getCurrentLanguage()
    
    // First, try to use embedded data
    if (embeddedData[currentLang] && embeddedData[currentLang].languages) {
      return embeddedData[currentLang].languages
    }
    
    // Fallback to dynamic import (for development)
    try {
      const languages = await import(/* @vite-ignore */ `../data/${currentLang}/languages.json`, {
        assert: { type: 'json' }
      })
      return languages.default
    } catch (importError) {
      // Fallback to fetch (for production)
      const baseUrl = window.location.origin
      const response = await fetch(`${baseUrl}/data/${currentLang}/languages.json`)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const languages = await response.json()
      return languages
    }
  } catch (error) {
    console.warn(`Could not load languages for ${getCurrentLanguage()}, falling back to English`, error)
    
    // Return embedded English data as final fallback
    if (embeddedData.en && embeddedData.en.languages) {
      return embeddedData.en.languages
    }
    
    console.error('Failed to load any languages')
    return []
  }
}

export const loadSubjects = async () => {
  try {
    const currentLang = getCurrentLanguage()
    
    // First, try to use embedded data
    if (embeddedData[currentLang] && embeddedData[currentLang].subjects) {
      return embeddedData[currentLang].subjects
    }
    
    // Fallback to dynamic import (for development)
    try {
      const subjects = await import(/* @vite-ignore */ `../data/${currentLang}/subjects.json`, {
        assert: { type: 'json' }
      })
      return subjects.default
    } catch (importError) {
      // Fallback to fetch (for production)
      const baseUrl = window.location.origin
      const response = await fetch(`${baseUrl}/data/${currentLang}/subjects.json`)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const subjects = await response.json()
      return subjects
    }
  } catch (error) {
    console.warn(`Could not load subjects for ${getCurrentLanguage()}, falling back to English`, error)
    
    // Return embedded English data as final fallback
    if (embeddedData.en && embeddedData.en.subjects) {
      return embeddedData.en.subjects
    }
    
    console.error('Failed to load any subjects')
    return []
  }
} 